// ============================================================
// Profileur : ce que la page d'un serveur demande, et quand
// ============================================================
// Fonctions pures (testées par profiler.page.test.ts). Quatre règles :
//
// 1. Les pics affichés sont ceux du monde affiché. Sans monde dans l'adresse, c'est le satellite qui
//    choisit le monde de la courbe (le plus mal en point, et à santé égale le plus chargé : la règle
//    de headlineLevel, dans profiler.health.ts) : les pics sont demandés pour CE monde, une fois la
//    courbe lue. Si le monde reste inconnu, ils sont demandés pour tous les mondes et la page le dit
//    (colonne « Monde », pas de nom de monde dans le titre).
// 2. La courbe n'est pas relue plus souvent qu'elle ne change : ses points font une minute sur une
//    heure, cinq minutes sur 6 et 24 heures, trente minutes sur 7 jours. Le voyant et les pics, eux,
//    sont relus à chaque relève.
// 3. La plus longue période proposée est celle que le satellite garde.
// 4. Une tuile ne dit rien que sa voisine contredit (monde figé, mesures arrêtées, démarrage).
// ============================================================
import type { EventList, LevelSummary, PeriodHours, Series, ServerDetail, ServerSummary } from './profiler.types';
import { fmtMs, fmtNumber, periodLabel } from './profiler.format.ts';
import { SPIKE_THRESHOLD_MS } from './profiler.health.ts';

type Res<T> = Promise<{ data?: T; error?: string; status?: number }>;
type Answer<T> = Awaited<Res<T>>;

/** Les trois lectures de la page (profiler.api.ts ; un faux dans les tests). */
export interface PageReads {
  getServer(key: string, group: string | null): Res<ServerDetail>;
  getSeries(key: string, level: string | null, hours: PeriodHours, group: string | null): Res<Series>;
  getEvents(key: string, level: string | null, limit: number, group: string | null): Res<EventList>;
}

export interface PageQuery {
  key: string;
  group: string | null;
  hours: PeriodHours;
  /** Monde choisi dans l'adresse (`?monde=`) ; null : celui que le satellite choisit. */
  levelParam: string | null;
}

export interface PageRead {
  detail: Answer<ServerDetail>;
  /** null : la courbe n'a pas été relue cette fois-ci (elle n'a pas pu changer). */
  series: Answer<Series> | null;
  events: Answer<EventList>;
  /** Monde pour lequel les pics ont été demandés ; null : tous les mondes. */
  eventsLevel: string | null;
}

/** Durée d'un point de la courbe selon la période : la relire plus souvent ne montrerait rien de neuf. */
export const SERIES_REFRESH_MS: Record<PeriodHours, number> = {
  1: 60_000,
  6: 5 * 60_000,
  24: 5 * 60_000,
  168: 30 * 60_000,
};

/** Rythme des relèves de la page d'un serveur : le voyant et les pics sont relus à chacune. */
export const POLL_MS = 30_000;

/**
 * La courbe doit-elle être relue à cette relève ? `loadedAt` : sa dernière lecture réussie, null si aucune.
 *
 * `loadedAt` est l'heure de la RÉPONSE, un peu après la relève qui l'a demandée. À la relève tombée une
 * minute plus tard, il s'est donc écoulé une minute moins ce temps de réponse : exiger la minute
 * entière repoussait la lecture d'une relève, et la courbe d'une heure était relue toutes les
 * 90 secondes au lieu de 60. La courbe est due dès la relève la plus proche de son échéance, à une
 * demi-relève près.
 */
export function seriesDue(loadedAt: number | null, hours: PeriodHours, now: number): boolean {
  return loadedAt == null || now - loadedAt >= SERIES_REFRESH_MS[hours] - POLL_MS / 2;
}

/**
 * Une relève de la page : le détail toujours, la courbe si `withSeries`, et les pics du monde affiché.
 *
 * @param shownLevel monde de la courbe déjà à l'écran, quand elle n'est pas relue
 */
export async function readServerPage(reads: PageReads, q: PageQuery, opts: { withSeries: boolean; shownLevel: string | null; eventsLimit: number }): Promise<PageRead> {
  const detail = reads.getServer(q.key, q.group);
  const series = opts.withSeries ? reads.getSeries(q.key, q.levelParam, q.hours, q.group) : null;

  let level = q.levelParam ?? (series ? null : opts.shownLevel);
  if (q.levelParam == null && series) {
    // Le monde affiché n'est connu qu'avec la courbe : les pics attendent sa réponse.
    level = (await series).data?.level ?? opts.shownLevel ?? null;
  }
  const events = reads.getEvents(q.key, level, opts.eventsLimit, q.group);
  return { detail: await detail, series: series ? await series : null, events: await events, eventsLevel: level };
}

/**
 * Ce que la liste des pics annonce : le monde dont elle parle, ou tous.
 *
 * @param eventsLevel monde pour lequel les pics ont été demandés ; null : tous les mondes
 * @param levelCount  nombre de mondes du serveur
 */
export function eventsScope(eventsLevel: string | null, levelCount: number): { label: string | null; levelColumn: boolean } {
  if (levelCount <= 1) return { label: null, levelColumn: false };
  if (eventsLevel == null) return { label: 'tous les mondes', levelColumn: true };
  return { label: `monde ${eventsLevel}`, levelColumn: false };
}

export interface PeriodChoice {
  hours: PeriodHours;
  /** Texte du bouton : « 1 heure », « 3 jours ». */
  label: string;
  /** Titre de la section : « Dernière heure », « 3 derniers jours ». */
  title: string;
}

/**
 * Périodes à proposer. La plus longue (7 jours) est bornée par la durée de garde de la courbe : avec
 * 3 jours de garde, le bouton dit « 3 jours » et le titre « 3 derniers jours », car c'est ce que le
 * satellite rend ; avec un jour de garde, elle ne dirait rien de plus que « 24 heures » et disparaît.
 *
 * @param retentionDays durée de garde de la courbe (ServerDetail.pointsRetentionDays) ; absente d'un
 *                      satellite plus ancien : 7 jours
 */
export function periodChoices(retentionDays: number | null | undefined): PeriodChoice[] {
  const days = keptDays(retentionDays);
  const out: PeriodChoice[] = [
    { hours: 1, label: '1 heure', title: periodLabel(1) },
    { hours: 6, label: '6 heures', title: periodLabel(6) },
    { hours: 24, label: '24 heures', title: periodLabel(24) },
  ];
  if (days > 1) out.push({ hours: 168, label: `${days} jours`, title: days === 7 ? periodLabel(168) : `${days} derniers jours` });
  return out;
}

/** Titre de la période affichée, même si elle n'est plus proposée (adresse écrite à la main). */
export function periodTitle(hours: PeriodHours, retentionDays: number | null | undefined): string {
  return periodChoices(retentionDays).find((p) => p.hours === hours)?.title ?? periodLabel(24);
}

function keptDays(retentionDays: number | null | undefined): number {
  if (retentionDays == null || !Number.isFinite(retentionDays) || retentionDays < 1) return 7;
  return Math.min(7, Math.floor(retentionDays));
}

// ---------------------------------------------------------------- ce que disent les tuiles « Maintenant »

/**
 * Sous « Tick le plus long » : « 95 % des ticks sous 10 ms ». Sans aucun tick fini sur la minute (monde
 * figé), le centile vaut 0 et la phrase disait « 95 % des ticks sous 0 ms » : elle cède la place à
 * « aucun tick fini », comme sous « Durée d'un tick ». Mesures arrêtées : rien, le monde tourne et ses
 * ticks finissent, ils ne sont simplement plus mesurés.
 */
export function longestTickHint(level: Pick<LevelSummary, 'ticks' | 'percentile95TickMs' | 'measuresStopped'>): string | undefined {
  if (level.ticks <= 0) return level.measuresStopped ? undefined : 'aucun tick fini';
  return level.percentile95TickMs != null ? `95 % des ticks sous ${fmtMs(level.percentile95TickMs)}` : undefined;
}

/**
 * Tuile « Pics (1 heure) » : le nombre, et la phrase dessous.
 *
 * Le satellite ne compte pas les premières minutes d'un serveur (il charge son monde) : pendant ce
 * temps le compteur vaut toujours 0. Afficher « 0, aucun tick de plus de 200 ms » à côté de « Tick le
 * plus long 3,9 s » était faux : la tuile dit alors que ces ticks ne sont pas comptés.
 *
 * @param starting le serveur est dans ses premières minutes (startingUp, profiler.health.ts)
 */
export function spikesHourTile(server: Pick<ServerSummary, 'spikesLastHour' | 'maxTickMsLastHour' | 'levels'>, starting: boolean): { value: string; hint: string } {
  if (starting) return { value: '-', hint: 'les ticks longs du démarrage ne sont pas comptés' };
  const what = server.spikesLastHour > 0 && server.maxTickMsLastHour != null
    ? `le plus long : ${fmtMs(server.maxTickMsLastHour)}`
    : `aucun tick de plus de ${SPIKE_THRESHOLD_MS} ms`;
  return { value: fmtNumber(server.spikesLastHour), hint: `${what}${server.levels.length > 1 ? ', tous mondes confondus' : ''}` };
}
